import { t } from 'i18next'
import IBAN from 'iban'
import * as Yup from 'yup'

import {
  addressValidationMonitorioSchema,
  addressValidationSchema
} from 'src/components/views/EscritosLexnet/components/shared/components/Intervinientes/components/forms/AddressForm'
import {
  checkTipoPersonaFisica,
  personaFisica
} from 'src/components/views/EscritosLexnet/components/shared/components/Intervinientes/components/types'
import { MIN_DATE } from 'src/constants/time'
import { FormField } from 'src/enums/Form'
import { TipoIdentificacion, TipoIntervencion, TipoPersona, TipoVia } from 'src/interfaces/Catalog'
import { CuentaBancariaTipo, emptyDireccion, Interviniente } from 'src/interfaces/EscritosLegales'
import { validDNI, validCIF, validNIE, validatSWIFTAlphanumeric } from 'src/utils/validations'

export const initialValues: Interviniente = {
  [FormField.INTERVINIENTE_FECHA_NACIMIENTO]: null,
  [FormField.INTERVINIENTE_IDENTIFICACION]: '',
  [FormField.INTERVINIENTE_IDENTIFICACION_TIPO]: null,
  [FormField.INTERVINIENTE_INTERVENCION_TIPO]: null,
  [FormField.INTERVINIENTE_LUGAR_NACIMIENTO]: '',
  [FormField.INTERVINIENTE_NACIONALIDAD]: null,
  [FormField.INTERVINIENTE_NOMBRE]: '',
  [FormField.INTERVINIENTE_NOMBRE_MADRE]: '',
  [FormField.INTERVINIENTE_NOMBRE_PADRE]: '',
  [FormField.INTERVINIENTE_PAIS_NACIMIENTO]: null,
  [FormField.INTERVINIENTE_PRIMER_APELLIDO]: '',
  [FormField.INTERVINIENTE_SEGUNDO_APELLIDO]: '',
  [FormField.INTERVINIENTE_SEXO]: null,
  [FormField.INTERVINIENTE_TELEFONO]: '',
  [FormField.INTERVINIENTE_EMAIL]: '',
  [FormField.INTERVINIENTE_PERSONA_TIPO]: personaFisica,
  [FormField.INTERVINIENTE_REPRESENTANTES_LEGALES]: [],
  [FormField.INTERVINIENTE_REPRESENTANTES_PROCESALES]: [],
  [FormField.INTERVINIENTE_DIRECCION]: emptyDireccion,
  [FormField.INTERVINIENTE_ADD_PROFESIONAL_REPRESENTANTE_PROCESAL]: false,
  [FormField.INTERVINIENTE_NO_INTERVIENE_OTRO_PROFESIONAL]: false,
  [FormField.INTERVINIENTE_CUENTA_BANCARIA_NIF]: '',
  [FormField.INTERVINIENTE_CUENTA_BANCARIA_NUMERO_CUENTA]: '',
  [FormField.INTERVINIENTE_CUENTA_BANCARIA_TITULAR]: '',
  [FormField.INTERVINIENTE_CUENTA_BANCARIA_TIPO]: CuentaBancariaTipo.IBAN
}

const NIF_CODIGO = 'N'
const DNI_CODIGO = 'D'
const NIE_CODIGO = 'X'
const CIF_CODIGO = 'C'
const idPersonaFisica = 1
export const INDOCUMENTADO_CODIGO = 'I'

export const validId = (): Yup.AnySchema => {
  return Yup.string()
    .nullable()
    .when(FormField.INTERVINIENTE_IDENTIFICACION_TIPO, {
      is: (tipo: TipoIdentificacion) => tipo?.codigo !== INDOCUMENTADO_CODIGO,
      then: Yup.string()
        .nullable()
        .required(t('form.validation.required.default'))
    })
    .when(FormField.INTERVINIENTE_IDENTIFICACION_TIPO, {
      is: (tipo: TipoIdentificacion) =>
        tipo?.codigo !== INDOCUMENTADO_CODIGO &&
        (tipo?.codigo === NIF_CODIGO || tipo?.codigo === DNI_CODIGO),
      then: Yup.string()
        .nullable()
        .test('validDNI', t('form.validation.not_valid_number'), (value, v) => {
          if (v.parent.personaTipo?.id === idPersonaFisica) {
            return value ? validDNI(value) : false
          }
          if (v.parent.personaTipo && v.parent.personaTipo.id !== idPersonaFisica) {
            return value ? validCIF(value) : false
          }
          return false
        })
    })
    .when(FormField.INTERVINIENTE_IDENTIFICACION_TIPO, {
      is: (tipo: TipoIdentificacion) =>
        tipo?.codigo !== INDOCUMENTADO_CODIGO && tipo?.codigo === CIF_CODIGO,
      then: Yup.string()
        .nullable()
        .test('validCIF', t('form.validation.not_valid_number'), value => {
          return value ? validCIF(value) : false
        })
    })
    .when(FormField.INTERVINIENTE_IDENTIFICACION_TIPO, {
      is: (tipo: TipoIdentificacion) =>
        tipo?.codigo !== INDOCUMENTADO_CODIGO && tipo?.codigo === NIE_CODIGO,
      then: Yup.string()
        .nullable()
        .test('validNIE', t('form.validation.not_valid_number'), value =>
          value ? validNIE(value) : false
        )
    })
}

export const commonFieldsSchema = (): any => ({
  [FormField.INTERVINIENTE_PERSONA_TIPO]: Yup.object()
    .nullable()
    .required(t('form.validation.required.default')),
  [FormField.INTERVINIENTE_INTERVENCION_TIPO]: Yup.object()
    .nullable()
    .required(t('form.validation.required.default')),
  [FormField.INTERVINIENTE_IDENTIFICACION_TIPO]: Yup.object()
    .nullable()
    .required(t('form.validation.required.default')),
  [FormField.INTERVINIENTE_IDENTIFICACION]: validId(),
  [FormField.INTERVINIENTE_NOMBRE]: Yup.string()
    .nullable()
    .required(t('form.validation.required.default'))
    .when(FormField.INTERVINIENTE_PERSONA_TIPO, {
      is: (tipoPersona: TipoPersona) => checkTipoPersonaFisica(tipoPersona),
      then: Yup.string()
        .nullable()
        .max(200, 'La razón social no puede tener más de 200 caracteres'),
      otherwise: Yup.string()
        .nullable()
        .max(50, 'El nombre no puede tener más de 50 caracteres')
    }),
  [FormField.INTERVINIENTE_PRIMER_APELLIDO]: Yup.string()
    .nullable()
    .max(100, 'El primer apellido no puede tener más de 100 caracteres')
    .when(FormField.INTERVINIENTE_PERSONA_TIPO, {
      is: (tipoPersona: TipoPersona) => !checkTipoPersonaFisica(tipoPersona),
      then: Yup.string()
        .nullable()
        .required(t('form.validation.required.default'))
    }),
  [FormField.INTERVINIENTE_SEGUNDO_APELLIDO]: Yup.string()
    .nullable()
    .max(50, 'El segundo apellido no puede tener más de 50 caracteres'),
  [FormField.INTERVINIENTE_LUGAR_NACIMIENTO]: Yup.string()
    .nullable()
    .max(50, 'El lugar de nacimiento no puede tener más de 50 caracteres'),
  [FormField.INTERVINIENTE_NOMBRE_PADRE]: Yup.string()
    .nullable()
    .max(50, 'El nombre del padre no puede tener más de 50 caracteres'),
  [FormField.INTERVINIENTE_NOMBRE_MADRE]: Yup.string()
    .nullable()
    .max(50, 'El nombre de la madre no puede tener más de 50 caracteres'),
  [FormField.INTERVINIENTE_EMAIL]: Yup.string().email(t('form.validation.email')),
  [FormField.INTERVINIENTE_TELEFONO]: Yup.string().matches(
    /^(\+\d{1,3})?(\d{9,15})$/,
    t('form.validation.telephone')
  ),
  [FormField.INTERVINIENTE_FECHA_NACIMIENTO]: Yup.date()
    .nullable()
    .typeError(t('form.validation.invalid_date'))
    .min(new Date(MIN_DATE), t('form.validation.invalid_date'))
    .max(new Date(), t('form.validation.max_date'))
})

export const validationSchema = (tipoVias?: TipoVia[]): Yup.ObjectSchema<any> => {
  return Yup.object().shape({
    ...commonFieldsSchema(),
    ...addressValidationSchema(tipoVias)
  })
}

export const validationSchemaIntervinienteMonitorio = (tipoVias?: TipoVia[]): Yup.AnySchema => {
  return Yup.object().shape({
    ...commonFieldsSchema(),
    ...addressValidationMonitorioSchema(tipoVias),
    [FormField.INTERVINIENTE_CUENTA_BANCARIA_TITULAR]: Yup.string()
      .nullable()
      .when(FormField.INTERVINIENTE_TIPO_INTERVENCION, {
        is: (tipoIntervencion: TipoIntervencion) => tipoIntervencion?.codigo === 'SOL',
        then: Yup.string()
          .nullable()
          .required(t('form.validation.required.default'))
      }),
    [FormField.INTERVINIENTE_CUENTA_BANCARIA_NIF]: Yup.string()
      .nullable()
      .when(FormField.INTERVINIENTE_TIPO_INTERVENCION, {
        is: (tipoIntervencion: TipoIntervencion) => tipoIntervencion?.codigo === 'SOL',
        then: Yup.string()
          .nullable()
          .required(t('form.validation.required.default'))
      })
      .test('validDNI', t('form.validation.not_valid_number'), value => {
        return value ? Boolean(validDNI(value) || validCIF(value)) : true
      }),
    [FormField.INTERVINIENTE_CUENTA_BANCARIA_NUMERO_CUENTA]: Yup.string()
      .nullable()
      .when(FormField.INTERVINIENTE_TIPO_INTERVENCION, {
        is: (tipoIntervencion: TipoIntervencion) => tipoIntervencion?.codigo === 'SOL',
        then: Yup.string()
          .nullable()
          .required(t('form.validation.required.default'))
      })
      .when(FormField.INTERVINIENTE_CUENTA_BANCARIA_TIPO, {
        is: (tipo: CuentaBancariaTipo) => tipo === CuentaBancariaTipo.IBAN,
        then: Yup.string()
          .nullable()
          .test('validIBAN', t('form.validation.not_valid_iban'), value => {
            return value ? IBAN.isValid(value) : true
          })
      })
      .when(FormField.INTERVINIENTE_CUENTA_BANCARIA_TIPO, {
        is: (tipo: CuentaBancariaTipo) => tipo === CuentaBancariaTipo.SWIFT,
        then: Yup.string()
          .nullable()
          .test('validSWIFT', t('form.validation.not_valid_swift'), value => {
            return value ? validatSWIFTAlphanumeric(value) : true
          })
      }),
    [FormField.INTERVINIENTE_REPRESENTANTES_PROCESALES]: Yup.array()
      .when(
        [
          FormField.INTERVINIENTE_TIPO_INTERVENCION,
          FormField.INTERVINIENTE_NO_INTERVIENE_OTRO_PROFESIONAL
        ],
        {
          is: (tipoIntervencion: TipoIntervencion, noIntervieneOtroProfesional: boolean) =>
            tipoIntervencion?.codigo === 'SOL' && noIntervieneOtroProfesional,
          then: Yup.array().min(1, t('form.validation.one_representante_procesal'))
        }
      )
      .when(
        [
          FormField.INTERVINIENTE_TIPO_INTERVENCION,
          FormField.INTERVINIENTE_NO_INTERVIENE_OTRO_PROFESIONAL
        ],
        {
          is: (tipoIntervencion: TipoIntervencion, noIntervieneOtroProfesional: boolean) =>
            tipoIntervencion?.codigo === 'SOL' && !noIntervieneOtroProfesional,
          then: Yup.array().min(2, t('form.validation.two_representantes_procesales'))
        }
      )
  })
}
